import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  map,
  tap,
  withLatestFrom,
  catchError,
  switchMap,
  concatMap,
  finalize,
  debounceTime
} from 'rxjs/operators';

import * as fromLabReq from './lab-req.reducer';
import * as LabReqActions from './lab-req.actions';
import * as LabReqSelectors from './lab-req.selectors';
import { APIService } from '../../services/api/api.service';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';

@Injectable()
export class LabReqEffects {
  loadLabReq$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabReqActions.loadLabReq),
      concatMap(action =>
        of(action).pipe(
          withLatestFrom(this.store.select(LabReqSelectors.getLimitOffset))
        )
      ),
      debounceTime(500),
      switchMap(([{ filters, loadType }, { limit, offset }]) => {
        const newOffset =
          loadType === 'older'
            ? offset + limit
            : loadType === 'new'
            ? 0
            : offset;
        return this.apiService
          .getLabReqs({ ...filters, limit, offset: newOffset })
          .pipe(
            tap(labReqs => {
              // Only surface "no more" when the user explicitly pages older
              // results. A fresh/new load returning empty just means this
              // customer has no lab requisitions — no toast needed.
              if (!labReqs.length && loadType === 'older') {
                this.notification.info('Info', 'No more lab requisitions.');
              }
            }),
            map(labReqs =>
              LabReqActions.loadLabReqSuccess({
                labReqs,
                loadType,
                limit,
                offset: newOffset
              })
            ),
            catchError(error => {
              console.error('Error', error);
              return of(LabReqActions.loadLabReqFailure({ error }));
            })
          );
      })
    )
  );

  saveLabReq$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabReqActions.saveLabReq),
      switchMap(action =>
        this.apiService.saveLabReq(action.labReq).pipe(
          tap(() => this.notification.success('Successfully saved.', '')),
          map(labReq => LabReqActions.saveLabReqSuccess({ labReq })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'There was an error saving drug.');
            return of(LabReqActions.saveLabReqFailure({ error }));
          })
        )
      )
    )
  );

  updateLabReq$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabReqActions.updateLabReq),
      switchMap(action =>
        this.apiService.updateLabReq(action.id, action.labReq).pipe(
          tap(() => this.notification.success('Successfully updated.', '')),
          map(labReq => LabReqActions.updateLabReqSuccess({ labReq })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error updating drug.'
            );
            return of(LabReqActions.updateLabReqFailure({ error }));
          })
        )
      )
    )
  );

  loadLabReqPdfUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabReqActions.loadLabReqPdfUrl),
      switchMap(action => {
        const loadingMessage = this.message.loading('Loading PDF...', {
          nzDuration: 0
        });
        return this.apiService.getLabReqPdfUrl(action.labReqId).pipe(
          tap(response => {
            if (response.success && response.signedUrl) {
              window.open(response.signedUrl, '_blank');
            } else {
              this.message.error('Failed to load PDF');
            }
          }),
          map(response => {
            if (response.success && response.signedUrl) {
              return LabReqActions.loadLabReqPdfUrlSuccess({
                signedUrl: response.signedUrl
              });
            } else {
              return LabReqActions.loadLabReqPdfUrlFailure({
                error: 'No signed URL returned'
              });
            }
          }),
          catchError(error => {
            console.error('Error loading lab req PDF:', error);
            this.message.error('Failed to load PDF. Please try again.');
            return of(LabReqActions.loadLabReqPdfUrlFailure({ error }));
          }),
          finalize(() => {
            loadingMessage.messageId &&
              this.message.remove(loadingMessage.messageId);
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService,
    private message: NzMessageService,
    private store: Store<fromLabReq.LabReqPartialState>
  ) {}
}
